import { memo, type ReactNode, useEffect, useRef, useState } from 'react';

import type { NavigationNode } from '../model/navigation-node';
import AppVersion from './app-version';
import { AppUpdateButton, AppUpdateProvider } from './app-update/app-update';
import { LanguageSelector } from './language-selector/language-selector';
import { Logout } from './logout/logout';
import { NavMenu } from './menu/menu';
import { ThemeOptions } from './theme-options/theme-options';
import { ThemeSelector } from './theme-selector/theme-selector';
import { UserButton } from './user-button/user-button';
import {
  ActionIcon,
  AppShell,
  Box,
  Button,
  Divider,
  Group,
  Popover,
  Stack,
  Text,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import IconSettings from '@tabler/icons-react/dist/esm/icons/IconSettings.mjs';
import IconSun from '@tabler/icons-react/dist/esm/icons/IconSun.mjs';
import { useTranslation } from 'react-i18next';
import classes from './navigation.module.css';

const OptionalAppUpdateProvider = ({ children }: { children: ReactNode }) =>
  __PWA_ENABLED__ ? (
    <AppUpdateProvider>{children}</AppUpdateProvider>
  ) : (
    children
  );

type NavigationProps = {
  currentRoute?: string;
  error: unknown;
  isFetching: boolean;
  navigation: NavigationNode[];
};

export const Navigation = memo(
  ({ currentRoute, error, isFetching, navigation }: NavigationProps) => {
    const [settingsOpened, setSettingsOpened] = useState(false);
    const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const { t } = useTranslation();

    const openSettings = () => {
      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
      }
      setSettingsOpened(true);
    };

    const closeSettings = () => {
      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
      }
      closeTimeout.current = setTimeout(() => {
        setSettingsOpened(false);
        closeTimeout.current = null;
      }, 150);
    };

    useEffect(
      () => () => {
        if (closeTimeout.current) {
          clearTimeout(closeTimeout.current);
        }
      },
      [],
    );

    return (
      <AppShell.Navbar
        id={'app-shell-navbar'}
        px='md'
        pb={'xs'}
        className={'!flex flex-col'}
      >
        <Box className={classes.section}>
          <UserButton />
        </Box>
        <OptionalAppUpdateProvider>
          <NavMenu
            currentRoute={currentRoute}
            error={error}
            isFetching={isFetching}
            navigation={navigation}
          />
          <AppVersion />
          <Group gap='xs' align='center' mt='md' wrap='nowrap'>
            <Box className='min-w-0 flex-1'>
              <Logout />
            </Box>
            <Popover
              opened={settingsOpened}
              onChange={setSettingsOpened}
              position='top-end'
              width={300}
              offset={8}
              shadow='md'
              withArrow
            >
              <Popover.Target>
                <ActionIcon
                  data-testid='nav-app-settings-toggle'
                  aria-label={t('app.appSettings')}
                  title={t('app.appSettings')}
                  color='gray'
                  variant='subtle'
                  size='lg'
                  onMouseEnter={openSettings}
                  onMouseLeave={closeSettings}
                  onFocus={openSettings}
                  onBlur={closeSettings}
                >
                  <IconSettings size={22} />
                </ActionIcon>
              </Popover.Target>
              <Popover.Dropdown
                data-testid='nav-app-settings-menu'
                onMouseEnter={openSettings}
                onMouseLeave={closeSettings}
                onFocusCapture={openSettings}
                onBlurCapture={closeSettings}
              >
                <Stack gap='sm'>
                  <Text size='sm' fw={600}>
                    {t('app.appSettings')}
                  </Text>
                  <Divider />
                  <LanguageSelector />
                  {__PWA_ENABLED__ && <AppUpdateButton />}
                  <Divider />
                  <Stack gap={6}>
                    <Text size='xs' c='dimmed' fw={500}>
                      {t('app.themeSettings.title')}
                    </Text>
                    <ThemeSelector />
                    <Button
                      leftSection={<IconSun size={16} />}
                      size='xs'
                      variant='default'
                      fullWidth
                      onClick={() =>
                        modals.open({
                          title: t('app.themeSettings.elements'),
                          children: <ThemeOptions />,
                        })
                      }
                    >
                      {t('app.themeSettings.elements')}
                    </Button>
                  </Stack>
                </Stack>
              </Popover.Dropdown>
            </Popover>
          </Group>
        </OptionalAppUpdateProvider>
      </AppShell.Navbar>
    );
  },
);